import type { HTMLAttributes, ReactNode } from 'react';

import { cx } from './cx';

/**
 * `title` is omitted from the DOM attributes and redefined: the HTML attribute
 * is a tooltip string, and a card's title is a heading that often carries a
 * pill beside it.
 */
export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Buttons or a pill, aligned to the top right of the header. */
  actions?: ReactNode;
  footer?: ReactNode;
  /** Off for a card whose child manages its own padding — a table, a list. */
  padded?: boolean | undefined;
  /**
   * Hover and press affordance only. It does NOT make the card focusable or
   * clickable: wrap it in a <Link> or put a real button in `actions`, because a
   * div with an onClick is unreachable from a keyboard.
   */
  interactive?: boolean | undefined;
  as?: 'div' | 'section' | 'article' | 'li' | undefined;
}

export function Card({
  title,
  subtitle,
  actions,
  footer,
  padded = true,
  interactive = false,
  as: Tag = 'div',
  className,
  children,
  ...rest
}: CardProps) {
  const hasHeader = title !== undefined || subtitle !== undefined || actions !== undefined;

  return (
    <Tag
      {...rest}
      className={cx(
        'dc-card',
        padded && 'dc-card--padded',
        interactive && 'dc-card--interactive',
        className,
      )}
    >
      {hasHeader && (
        <div className="dc-card__header">
          <div className="dc-card__heading">
            {title !== undefined && <div className="dc-card__title">{title}</div>}
            {subtitle !== undefined && <div className="dc-card__subtitle">{subtitle}</div>}
          </div>
          {actions !== undefined && <div className="dc-card__actions">{actions}</div>}
        </div>
      )}
      {children !== undefined && <div className="dc-card__body">{children}</div>}
      {footer !== undefined && <div className="dc-card__footer">{footer}</div>}
    </Tag>
  );
}
